<template>
    <MainContent>
        <template #title>
            Cleaning Items
        </template>
        <template #subtitle>
            Wanon Niwat Hospital [WEBPORTAL WNH]
        </template>
        <template #breadcrumb>
            Dashboard
        </template>
        <template #header>
            <Overlay :showOverlay="!items.length" />
            <div class="tile-title-w-btn">
                <h3 class="title">All Cleaning Items</h3>
                <p>
                    <a class="btn btn-primary icon-btn" href="#" @click="openAddModal">
                        <i class="bi bi-plus-square me-2"></i> Add Item
                    </a>
                </p>
            </div>
        </template>
        <template #content>
            <div class="tile-body">
                <table class="table table-striped table-bordered">
                    <thead>
                        <tr>
                            <th scope="col">#</th>
                            <th scope="col">Item Name</th>
                            <th scope="col">Checklist</th>
                            <th scope="col">Manage</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, index) in items" :key="item.id">
                            <th scope="row">{{ index + 1 }}</th>
                            <td>{{ item.name }}</td>
                            <td>{{ item.checklist.name }}</td>
                            <td>
                                <button class="btn btn-primary btn-sm me-2" @click="openEditModal(item)">
                                    <i class="bi bi-pencil"></i> Edit
                                </button>
                                <button class="btn btn-danger btn-sm" @click="handleDelete(item.id)">
                                    <i class="bi bi-trash"></i> Delete
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <!-- Modal -->
                <div class="modal fade" id="itemModal" tabindex="-1" aria-labelledby="modalLabel" aria-hidden="true">
                    <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title" id="modalLabel">{{ modalMode === 'edit' ? 'Edit Item' : 'Add Item' }}</h5>
                                <button type="button" class="btn-close" aria-label="Close" @click="hideModal"></button>
                            </div>
                            <form @submit.prevent="handleSubmit(editingItem)">
                                <div class="modal-body">
                                    <div class="mb-3">
                                        <label for="name" class="form-label">Item Name</label>
                                        <input type="text" id="name" class="form-control" v-model="editingItem.name" required />
                                    </div>
                                    <div class="mb-3">
                                        <label for="checklist" class="form-label">Checklist</label>
                                        <select id="checklist" class="form-select" v-model="editingItem.checklist_id" required>
                                            <option v-for="list in checklists" :key="list.id" :value="list.id">
                                                {{ list.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                                <div class="modal-footer">
                                    <button type="button" class="btn btn-secondary" @click="hideModal">Close</button>
                                    <button type="submit" class="btn btn-primary">Save changes</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </MainContent>
</template>

<script>
import { Modal } from 'bootstrap';
import { reactive, nextTick, ref, onMounted } from 'vue';
import axiosInstance from '@/utils/axiosInstance';
import {
    // showSuccessAlert,
    showErrorAlert,
    showCreateAlert,
    showUpdateAlert,
    showDeleteAlert,
    showConfirm
} from '@/utils/sweetalert2-utils';
import MainContent from '@/components/MainContent.vue';
import Overlay from '../components/Overlay.vue';

export default {
    name: 'CleaningItems',
    components: {
        MainContent,
        Overlay
    },
    setup() {
        const items = ref([]);
        const checklists = ref([]);
        const modalMode = ref('add');
        const editingItem = reactive({
            name: '',
            checklist_id: null
        });

        const fetchData = async () => {
            try {
                const response = await axiosInstance.get('/cleaning_items');
                items.value = response.data;
            } catch (error) {
                showErrorAlert('Failed to load data');
            }
        };

        const fetchChecklists = async () => {
            try {
                const response = await axiosInstance.get('/checklists');
                checklists.value = response.data.filter(item => [4, 5].includes(item.id));
            } catch (error) {
                showErrorAlert('Failed to load checklists');
            }
        };

        const handleDelete = async (id) => {
            const result = await showConfirm();
            if (result.isConfirmed) {
                try {
                    await axiosInstance.delete(`/cleaning_items/${id}`);
                    items.value = items.value.filter(item => item.id !== id);
                    showDeleteAlert();
                } catch (error) {
                    showErrorAlert('Error deleting item');
                }
            }
        };

        const handleSubmit = async (updatedItem) => {
            try {
                if (modalMode.value === 'edit') {
                    await axiosInstance.put(`/cleaning_items/${updatedItem.id}`, updatedItem);
                    showUpdateAlert();
                } else {
                    await axiosInstance.post('/cleaning_items', updatedItem);
                    showCreateAlert();
                }
                fetchData();
                // showSuccessAlert()
                hideModal();
            } catch (error) {
                showErrorAlert('Error saving item');
            }
        };

        const showModal = () => {
            nextTick(() => {
                const modalElement = document.getElementById('itemModal');
                if (modalElement) {
                    const modal = new Modal(modalElement);
                    modal.show();
                }
            });
        };

        const hideModal = () => {
            const modalElement = document.getElementById('itemModal');
            const modal = Modal.getInstance(modalElement);
            if (modal) modal.hide();
        };

        const openAddModal = () => {
            Object.assign(editingItem, { id: null, name: '', checklist_id: null });
            modalMode.value = 'add';
            showModal();
        };

        const openEditModal = (item) => {
            Object.assign(editingItem, item);
            modalMode.value = 'edit';
            showModal();
        };

        onMounted(() => {
            fetchData();
            fetchChecklists();
        });

        return {
            items,
            checklists,
            editingItem,
            modalMode,
            handleDelete,
            showModal,
            hideModal,
            openAddModal,
            openEditModal,
            handleSubmit
        };
    }
};
</script>
